import { firestore } from '@/services/firebase'
import API from '@/services/api'

export function createdById(value) {
    let current = value
    const seen = new Set()
    while (current != null && current !== '') {
        if (typeof current === 'string' || typeof current === 'number') {
            return String(current)
        }
        if (Array.isArray(current)) {
            for (const entry of current) {
                const id = createdById(entry)
                if (id) {
                    return id
                }
            }
            return ''
        }
        if (typeof current === 'object') {
            if (seen.has(current)) {
                return ''
            }
            seen.add(current)
            current = current.id
            continue
        }
        return ''
    }
    return ''
}

export function createdByLabel(value) {
    if (value == null || value === '') {
        return ''
    }
    if (typeof value === 'string') {
        return value
    }
    if (Array.isArray(value)) {
        for (const entry of value) {
            if (
                entry &&
                typeof entry === 'object' &&
                typeof entry.name === 'string' &&
                entry.name
            ) {
                return entry.name
            }
        }
        return createdById(value)
    }
    if (typeof value === 'object') {
        if (typeof value.name === 'string' && value.name) {
            return value.name
        }
        return createdById(value)
    }
    return ''
}

const activeWorkOrdersBackupCache = new Map()

function logWoLoad(label, details) {
    if (process.env.NODE_ENV === 'production') {
        return
    }
    console.info(`[WO load] ${label}`, details)
}

function rememberBackup(cacheKey, entry) {
    const prefix = cacheKey.slice(0, cacheKey.lastIndexOf('|'))
    for (const key of activeWorkOrdersBackupCache.keys()) {
        if (key !== cacheKey && key.startsWith(`${prefix}|`)) {
            activeWorkOrdersBackupCache.delete(key)
        }
    }
    activeWorkOrdersBackupCache.set(cacheKey, entry)
}

function copyWorkOrders(workOrders) {
    return workOrders.map(workOrder => ({ ...workOrder }))
}

class WOMethods {
    constructor() {
        this.users = {}
    }

    matchesStatusFilter(workOrder, statusFilter) {
        if (!statusFilter) {
            return true
        }
        if (Array.isArray(statusFilter)) {
            return statusFilter.includes(workOrder.status)
        }
        return workOrder.status === statusFilter
    }

    filterByStatus(workOrders, statusFilter) {
        if (!statusFilter) {
            return workOrders
        }
        return workOrders.filter(wo =>
            this.matchesStatusFilter(wo, statusFilter)
        )
    }

    async bootstrapWoLookups({ includeSettings = false } = {}) {
        const requests = [
            API.getMyInfo(),
            API.getWoProjects(),
            API.getWoUsers(),
        ]
        if (includeSettings) {
            requests.push(API.getSettings())
        }
        const results = await Promise.all(requests)
        const {
            data: { user },
        } = results[0]
        const projects = results[1]
        const {
            data: { users },
        } = results[2]
        const settings = includeSettings
            ? results[3]?.data?.settings
            : undefined
        return { user, projects, users, settings }
    }

    createdById(value) {
        return createdById(value)
    }

    createdByLabel(value) {
        return createdByLabel(value)
    }

    assignCreatedBy(workOrder, users) {
        if (!workOrder) {
            return ''
        }
        const id = createdById(workOrder.createdBy)
        const current = workOrder.createdBy
        const hasName =
            current &&
            typeof current === 'object' &&
            !Array.isArray(current) &&
            typeof current.name === 'string' &&
            current.name
        const user = id && users ? users[id] : undefined
        if (user && typeof user === 'object' && !Array.isArray(user)) {
            if (user.name || !hasName) {
                workOrder.createdBy = user
                return id
            }
        }
        if (hasName) {
            return id
        }
        workOrder.createdBy = {
            id,
            name: '',
            permissions: [],
            process: [],
        }
        if (id && users && !users[id]) {
            users[id] = workOrder.createdBy
        }
        return id
    }

    async hydrateWorkOrderOnOpen(companyId, item) {
        if (!item || item.partNumbers || item.status === 'deleted') {
            return item
        }
        const doc = await firestore
            .collection('companies')
            .doc(companyId)
            .collection('workOrders')
            .doc(item.id)
            .get()
        if (!doc.exists) {
            return item
        }
        return { id: doc.id, ...doc.data() }
    }

    async mapInBatches(items, mapper, batchSize = 25) {
        const out = []
        for (let i = 0; i < items.length; i += batchSize) {
            const chunk = await Promise.all(
                items.slice(i, i + batchSize).map(mapper)
            )
            chunk.forEach(item => {
                if (item) {
                    out.push(item)
                }
            })
        }
        return out
    }

    async loadAllActiveWorkOrders(
        companyId,
        statusFilter = null,
        useLiteVersion = false,
        watchStatusFilter = null
    ) {
        // Retrieve the backup.json file from Firebase Storage
        const startedAt = performance.now()
        const storage = firestore.app.firebase_.storage()
        let path = `/companies/${companyId}/workOrders/backup.json`
        if (useLiteVersion) {
            path = `/companies/${companyId}/workOrders/backup-lite.json`
        }

        try {
            const deletedPromise = API.getDeletedWorkOrdersIds().catch(e => {
                console.error('Error fetching deleted work order ids:', e)
                return []
            })
            const fileRef = storage.ref(path)
            const [url, metadata] = await Promise.all([
                fileRef.getDownloadURL(),
                fileRef.getMetadata(),
            ])
            const cacheKey = `${companyId}|${path}|${metadata.updated ||
                metadata.timeCreated}`
            const cached = activeWorkOrdersBackupCache.get(cacheKey)
            const filePromise = cached
                ? Promise.resolve({
                      workOrders: copyWorkOrders(cached.workOrders),
                      payloadBytes: cached.payloadBytes,
                      fromCache: true,
                  })
                : fetch(url)
                      .then(response => response.text())
                      .then(text => {
                          let parsed = JSON.parse(text)
                          if (typeof parsed === 'string') {
                              try {
                                  parsed = JSON.parse(parsed)
                              } catch (e) {
                                  console.error(
                                      'Failed to parse JSON response:',
                                      e
                                  )
                                  parsed = []
                              }
                          }
                          if (!Array.isArray(parsed)) {
                              parsed = []
                          }
                          rememberBackup(cacheKey, {
                              workOrders: parsed,
                              payloadBytes: text.length,
                          })
                          return {
                              workOrders: copyWorkOrders(parsed),
                              payloadBytes: text.length,
                              fromCache: false,
                          }
                      })
            const [fileResult, deletedWorkOrdersIdsRaw] = await Promise.all([
                filePromise,
                deletedPromise,
            ])
            let workOrders = fileResult.workOrders

            // Drop non-matching statuses before extra filtering so nested
            // partNumbers from other tabs are not kept in memory.
            workOrders = this.filterByStatus(workOrders, statusFilter)

            let deletedWorkOrdersIds = Array.isArray(deletedWorkOrdersIdsRaw)
                ? deletedWorkOrdersIdsRaw
                : []

            const deletedIdsSet = new Set(deletedWorkOrdersIds)
            workOrders = workOrders.filter(
                wo => wo && wo.id && !deletedIdsSet.has(wo.id)
            )

            const referenceDateString = metadata.updated || metadata.timeCreated
            const yesterday = new Date(referenceDateString)
            yesterday.setHours(yesterday.getHours() - 1)

            // Query SDK for workOrders updated after the backup window.
            // watchStatusFilter must include destination statuses (e.g. approved
            // when listing sent) so a status change replaces the dump copy and
            // can be dropped from the view. statusFilter is only the list.
            const deltaStatusFilter = watchStatusFilter || statusFilter
            let sdkWorkOrders = []
            try {
                let query = firestore
                    .collection('companies')
                    .doc(companyId)
                    .collection('workOrders')
                    .where('updatedOn', '>', yesterday)

                if (deltaStatusFilter) {
                    if (Array.isArray(deltaStatusFilter)) {
                        query = query.where('status', 'in', deltaStatusFilter)
                    } else {
                        query = query.where('status', '==', deltaStatusFilter)
                    }
                }

                const snapshot = await query.orderBy('updatedOn', 'desc').get()
                snapshot.forEach(doc => {
                    sdkWorkOrders.push({ id: doc.id, ...doc.data() })
                })
            } catch (e) {
                console.error('Error fetching SDK work orders:', e)
            }

            // Merge non-duplicated entries (by ID) from SDK with workOrders from storage
            const mergedIds = new Set(workOrders.map(wo => wo.id))
            sdkWorkOrders.forEach(sdkWO => {
                if (deletedIdsSet.has(sdkWO.id)) {
                    return
                }
                if (!mergedIds.has(sdkWO.id)) {
                    workOrders.push(sdkWO)
                } else {
                    const idx = workOrders.findIndex(wo => wo.id === sdkWO.id)
                    if (idx !== -1) {
                        workOrders[idx] = sdkWO
                    }
                }
            })

            const filtered = this.filterByStatus(workOrders, statusFilter)
            logWoLoad('backup', {
                path,
                ms: Math.round(performance.now() - startedAt),
                rows: filtered.length,
                payloadBytes: fileResult.payloadBytes,
                fromCache: fileResult.fromCache,
            })
            return filtered
        } catch (err) {
            logWoLoad('backup-error', {
                path,
                ms: Math.round(performance.now() - startedAt),
                message: err && err.message,
            })
            return []
        }
    }

    setProcessesInfo({ workOrder, settingProcesses }) {
        workOrder.processesInfo = []
        workOrder.items = workOrder.items ? workOrder.items : []
        workOrder.items.forEach(item => {
            if (item.processes) {
                item.processes.forEach(process => {
                    let processInfo = workOrder.processesInfo.find(
                        info => info.process.id == process
                    )

                    if (!processInfo) {
                        let totalDelivered = item.deliveries
                            ? item.deliveries.reduce(
                                  (total, register) =>
                                      register.prevProcess.id == process
                                          ? total + register.qty
                                          : total,
                                  0
                              )
                            : 0
                        let totalProduced = item.production
                            ? item.production.reduce(
                                  (total, register) =>
                                      register.process.id == process
                                          ? total + register.qty
                                          : total,
                                  0
                              )
                            : 0
                        let processIndex = settingProcesses?.processes?.findIndex(
                            p => p?.id == process
                        )

                        workOrder.processesInfo.push({
                            process: {
                                id:
                                    settingProcesses?.processes[processIndex]
                                        ?.id,
                                name:
                                    settingProcesses?.processes[processIndex]
                                        ?.name,
                            },
                            qty: item.quantity,
                            delivered: totalDelivered,
                            produced: totalProduced,
                        })
                    } else {
                        processInfo.qty += item.quantity
                        processInfo.delivered += item.deliveries
                            ? item.deliveries.reduce(
                                  (total, register) =>
                                      register.prevProcess.id == process
                                          ? total + register.qty
                                          : total,
                                  0
                              )
                            : 0
                        processInfo.produced += item.production
                            ? item.production.reduce(
                                  (total, register) =>
                                      register.process.id == process
                                          ? total + register.qty
                                          : total,
                                  0
                              )
                            : 0
                    }
                })
            }
        })
    }

    formatItems(workOrder) {
        if (
            !(
                (workOrder.status == 'closed' ||
                    workOrder.status == 'cancelled') &&
                !workOrder.partNumbers
            )
        ) {
            if (Array.isArray(workOrder.items)) {
                workOrder.items.length = 0
            } else {
                workOrder.items = []
            }
        }
        if (workOrder.partNumbers) {
            Object.keys(workOrder.partNumbers).forEach(partNumberId => {
                let item = {
                    partNumberId,
                }
                Object.keys(workOrder.partNumbers[partNumberId]).forEach(
                    key => {
                        if (key == 'assemblyItems') {
                            let assemblyItems = []
                            Object.keys(
                                workOrder.partNumbers[partNumberId][key]
                            ).forEach(aiKey => {
                                assemblyItems.push({
                                    partNumberId: aiKey,
                                    ...workOrder.partNumbers[partNumberId][key][
                                        aiKey
                                    ],
                                })
                            })

                            item[key] = assemblyItems
                        } else {
                            item[key] = workOrder.partNumbers[partNumberId][key]
                        }
                    }
                )

                workOrder.items.push(item)
            })
        }
        workOrder.formatted = true
        return workOrder
    }
}

export default WOMethods
